<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Availability for {{ pmsConnDetails.usr }}
          </v-card-title>
          <v-card-subtitle
            >{{ pmsConnDetails.pmsType }} - Processed
            {{ availabilityEpoch }}</v-card-subtitle
          >
          <v-tabs fixed-tabs>
            <v-tab>Summary</v-tab>
            <v-tab>Calendar View</v-tab>
            <v-tab>JSON View</v-tab>

            <v-tab-item class="pa-6">
              <v-simple-table>
                <tbody>
                  <tr>
                    <td>Sync Period</td>
                    <td>
                      {{ availability.syncStartDate | toDate }} -
                      {{ availability.syncEndDate | toDate }} (
                      {{ availability.syncDays }} days )
                    </td>
                  </tr>
                  <tr>
                    <td>PMS Server Time Uploaded</td>
                    <td>{{ new Date(availability.epochTs) }}</td>
                  </tr>
                  <tr>
                    <td>Appointment Book Stats</td>
                    <td>
                      <ul>
                        <li>Total Slots: {{ slotStats.totalSlots }}</li>
                        <li
                          v-for="bookStats in slotStats.bookDetails"
                          :key="bookStats.pmsBookFQId"
                        >
                          {{ bookStats.pmsBookName || bookStats.pmsBookFQId }}
                          <ul>
                            <li>Available Slots: {{ bookStats.slotCount }}</li>
                            <li>
                              Available Time:
                              {{ bookStats.availableSeconds | secondsToHms }}
                            </li>
                            <li>
                              Default Slot Size:
                              {{
                                parseDurationString(
                                  bookStats.defaultBookSlotDuration
                                ) | secondsToHms
                              }}
                            </li>
                            <li v-if="bookStats.slotCount > 0">
                              Average Slot Size:
                              {{ ( bookStats.availableSeconds / bookStats.slotCount ) | secondsToHms
                              }}
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>
            <v-tab-item class="pa-6">
              <v-row class="fill-height">
                <v-col>
                  <v-sheet height="64">
                    <v-toolbar flat color="white">
                      <v-btn
                        outlined
                        class="mr-4"
                        color="grey darken-2"
                        @click="setToday"
                        >Today</v-btn
                      >
                      <v-btn fab text small color="grey darken-2" @click="prev">
                        <v-icon small>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn fab text small color="grey darken-2" @click="next">
                        <v-icon small>mdi-chevron-right</v-icon>
                      </v-btn>
                      <v-toolbar-title v-if="$refs.calendar">{{
                        $refs.calendar.title
                      }}</v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-menu bottom right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            outlined
                            color="grey darken-2"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <span>{{ displayTypeLabel }}</span>
                            <v-icon right>mdi-menu-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="setDisplayType('Summary')">
                            <v-list-item-title>Summary</v-list-item-title>
                          </v-list-item>

                          <v-list-item
                            v-for="book of availability.books"
                            :key="book.pmsBookFQId"
                            @click="setDisplayType(book.pmsBookFQId)"
                          >
                            <v-list-item-title>{{
                              book.pmsBookName || book.pmsBookFQId
                            }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-spacer></v-spacer>
                      <v-menu bottom right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            outlined
                            color="grey darken-2"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <span>{{ typeToLabel[type] }}</span>
                            <v-icon right>mdi-menu-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="type = 'day'">
                            <v-list-item-title>Day</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="type = 'week'">
                            <v-list-item-title>Week</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="type = 'month'">
                            <v-list-item-title>Month</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="type = '4day'">
                            <v-list-item-title>4 days</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-toolbar>
                  </v-sheet>
                  <v-sheet height="600">
                    <v-calendar
                      ref="calendar"
                      v-model="focus"
                      color="primary"
                      :events="events"
                      :event-color="getEventColor"
                      :type="type"
                      @click:event="showEvent"
                      @click:more="viewDay"
                      @click:date="viewDay"
                      @change="updateRange"
                      :start="availability.syncStartDate"
                      :end="availability.syncEndDate"
                    >
                      <v-calendar-monthly
                        :start="availability.syncStartDate"
                        :end="availability.syncEndDate"
                      ></v-calendar-monthly>
                    </v-calendar>
                    <v-menu
                      v-model="selectedOpen"
                      :close-on-content-click="false"
                      :activator="selectedElement"
                      offset-x
                    >
                      <v-card color="grey lighten-4" min-width="350px" flat>
                        <v-toolbar :color="selectedEvent.color" dark>
                          <v-toolbar-title
                            v-html="selectedEvent.name"
                          ></v-toolbar-title>
                          <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-card-text>
                          <span v-html="selectedEvent.details"></span>
                        </v-card-text>
                        <v-card-actions>
                          <v-btn
                            text
                            color="secondary"
                            @click="selectedOpen = false"
                            >Close</v-btn
                          >
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-tab-item>
            <v-tab-item class="pa-6">
              <json-viewer
                :expand-depth="3"
                copyable
                boxed
                sort
                :value="availability"
                expanded
              ></json-viewer>
            </v-tab-item>
          </v-tabs>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "@/graphql/queries";

export default {
  props: {
    pmsConnDetails: Object,
  },
  name: "availability",
  watch: {},
  computed: {
    availabilityEpoch: function () {
      if (!this.key) {
        return;
      }

      const keyWithFileName = this.key.substr(this.key.lastIndexOf("/") + 1);
      const fileName = keyWithFileName.substring(0, keyWithFileName.length - 5);

      const epoch = Date(Number(fileName));
      return epoch;
    },
    displayTypeLabel: function () {
      if (
        this.calendarAvailabilityDisplayType == "Summary" ||
        (this.availability?.books?.length ?? 0) == 0
      ) {
        return this.calendarAvailabilityDisplayType;
      }

      const book = this.availability.books.find(
        (b) => b.pmsBookFQId == this.calendarAvailabilityDisplayType
      );

      if (!book) {
        this.calendarAvailabilityDisplayType;
      }

      return book.pmsBookName ?? book.pmsBookFQId;
    },
    slotStats: function () {
      if (!this.availability) {
        return {};
      }

      const bookCount = this.availability?.books?.length ?? 0;
      if (bookCount == 0) {
        return {};
      }
      const bookDetails = this.availability?.books?.map((b) => {
        return {
          pmsBookName: b.pmsBookName,
          pmsBookFQId: b.pmsBookFQId,
          defaultBookSlotDuration: b.defaultBookSlotDuration,
          slotCount:
            b.days
              ?.map((d) => d.slots?.length || 0)
              ?.reduce((sa, sb) => sa + sb, 0) ?? 0,
          availableSeconds:
            b.days
              ?.map((d) =>
                d.slots
                  ?.map((s) => this.parseDurationString(s.d))
                  ?.reduce((  ssa, ssb) => ssa + ssb, 0)
              )
              ?.reduce((sa, sb) => sa + sb, 0) ?? 0,
        };
      });
      const totalSlots = bookDetails
        .map((bd) => bd.slotCount)
        .reduce((bda, bdb) => bda + bdb, 0);

      return {
        bookCount,
        bookDetails: bookDetails.sort((bd) => bd.pmsBookName ?? bd.pmsBookFQId),
        totalSlots,
      };
    },
  },
  data: function () {
    return {
      sub: "",
      key: "",
      status: {},
      availabilityData: {},
      availability: {},
      calendarAvailabilityDisplayType: "Summary",
      calendarAvailabilityDisplayBook: "",
      focus: "",
      type: "month",
      typeToLabel: {
        month: "Month",
        week: "Week",
        day: "Day",
        "4day": "4 Days",
      },
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      intervalMinutes: 15,
      events: [],
      colors: [
        "blue",
        "indigo",
        "deep-purple",
        "cyan",
        "green",
        "orange",
        "grey darken-1",
      ],
      names: [
        "Meeting",
        "Holiday",
        "PTO",
        "Travel",
        "Event",
        "Birthday",
        "Conference",
        "Party",
      ],
    };
  },
  created: async function () {},
  mounted() {
    this.sub = this.$route.params.sub;
    this.key = this.$route.params.key;
    this.getStatus(this.sub);
    this.getAvailabilityData(this.sub, this.key);
    if (this.$refs?.calendar) {
      this.$refs.calendar.checkChange();
    }
  },
  filters: {
    toDate(inDt) {
      return new Date(inDt).toDateString();
    },
    secondsToHms(d) {
      d = Number(d);
      var h = Math.floor(d / 3600);
      var m = Math.floor((d % 3600) / 60);
      var s = Math.floor((d % 3600) % 60);

      var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
      var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " minutes, ") : "";
      var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";
      return hDisplay + mDisplay + sDisplay;
    },
  },
  methods: {
    async getStatus(sub) {
      try {
        console.log({ msg: "getStatus", sub });
        const status = await API.graphql(
          graphqlOperation(queries.getStatus, { sub: sub })
        );

        if ((status?.data?.getStatus?.usr ?? "") === "") {
          this.handleResponseError("Get Connector Info", status.data);
          return;
        }

        this.status = status.data.getStatus;
        console.log("got status", this.status);
      } catch (err) {
        this.handleResponseError("Get Connector Info", err);
        this.status = {};

        console.log("getstatus ex", err);
      }
    },
    async getAvailabilityData(sub, key) {
      try {
        console.log("getAvailabilityData", {
          msg: "getAvailabilityData",
          sub,
          key,
        });
        const availabilityDataResponse = await API.graphql(
          graphqlOperation(queries.getAvailabilityFileContent, {
            sub: sub,
            key: key,
          })
        );

        if (
          (availabilityDataResponse?.data?.getAvailabilityFileContent?.ETag ??
            "") === ""
        ) {
          this.handleResponseError(
            "Get Availability Data Request Failed",
            availabilityDataResponse.data
          );
          return;
        }

        this.availabilityData =
          availabilityDataResponse.data.getAvailabilityFileContent;

        if (this.availabilityData.contents) {
          this.availability = JSON.parse(this.availabilityData.contents);
          this.focus = this.availability.syncStartDate;

          this.populateEvents();
        } else {
          this.availability = {};
          this.events = [];
        }

        console.log("got availability data", this.availabilityData);
      } catch (err) {
        this.handleResponseError("Get Availability Data Request Failed", err);
        this.availabilityData = {};

        console.log("getAvailabilityData ex", err);
      }
    },
    populateEvents() {
      if (!this.availability?.books) {
        return;
      }

      this.events = [];

      const displayByBook = this.availability.books.find(
        (b) => b.pmsBookFQId == this.calendarAvailabilityDisplayType
      );
      /*
      if (displayByBook?.defaultBookSlotDuration) {
        this.intervalMinutes =
          parseDurationString(displayByBook.defaultBookSlotDuration) / 60;
      }
*/
      for (
        let bookIndex = 0;
        bookIndex < this.availability.books?.length;
        bookIndex++
      ) {
        const book = this.availability.books[bookIndex];

        if (displayByBook && book.pmsBookFQId != displayByBook.pmsBookFQId) {
          continue;
        }

        for (
          let bookDayIndex = 0;
          bookDayIndex < book.days?.length;
          bookDayIndex++
        ) {
          const bookDay = book.days[bookDayIndex];
          if (displayByBook) {
            if (bookDay.slots && bookDay.slots.length > 0) {
              for (const slot of bookDay.slots) {
                const durationSeconds = this.parseDurationString(slot.d);
                const startTime = new Date(`${bookDay.date} ${slot.s}`);
                const endTime = new Date(
                  startTime.getTime() + durationSeconds * 1000
                );

                const e = {
                  name: `Free Slot - ${this.secondsToHms(durationSeconds)}`,
                  /*
                name: `${slot.s} - ${endTime.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false
                })}`,*/
                  start: startTime,
                  end: endTime,
                  color: this.colors[bookIndex % this.colors.length],
                  timed: true,
                  details: slot.m ? `Metadata: ${JSON.stringify(slot.m)}` : "",
                };

                this.events.push(e);
              }
            }

            if (!bookDay.sessions) {
              continue;
            }

            for (const s of bookDay.sessions) {
              const startTime = new Date(`${bookDay.date} ${s.s}`);
              const endTime = new Date(`${bookDay.date} ${s.e}`);
              const session = {
                name: s.u ? "Session - Unavailable" : "Session",
                start: startTime,
                end: endTime,
                color: this.colors[bookIndex + (1 % this.colors.length)],
                timed: true,
                details: `${
                  s.u ? "Unavailble Time" : "Available Time"
                } Default Slot Duration: ${
                  s.d ? this.parseDurationString(s.d) : "n/a"
                } ${s.m ? `Metadata: ${JSON.stringify(s.m)}` : ""}`,
              };

              this.events.push(session);
            }
          } else {
            // Summary
            const slotsThisDay = bookDay.slots?.length ?? 0;
            const sessionsThisDay = bookDay.sessions?.length ?? 0;

            const thisDaySummary = {
              name: `${
                book.pmsBookName ?? book.pmsBookFQId
              } - ${slotsThisDay} Slot${
                slotsThisDay != 1 ? "s" : ""
              } ${sessionsThisDay} Session${sessionsThisDay != 1 ? "s" : ""}`,
              start: bookDay.date,
              color: this.colors[bookIndex % this.colors.length],
              timed: false,
              details: `First Slot: ${bookDay.slots?.length ?? 0 > 0 ? bookDay.slots[0].s : "n/a"}\nLast Slot ${ 
                bookDay.slots?.length ?? 0 > 0 ? bookDay.slots[bookDay.slots.length - 1].s : "n/a"
              }`,
            };

            this.events.push(thisDaySummary);
          }
        }
      }
    },
    setDisplayType(selectedDisplayType) {
      if (selectedDisplayType == this.calendarAvailabilityDisplayType) {
        return;
      }

      this.calendarAvailabilityDisplayType = selectedDisplayType;

      this.populateEvents();
    },
    async handleResponseError(operationDescription, response) {
      console.log("handleResponseError", response);
      const notifyResult = await this.$dialog.notify.error(
        operationDescription,
        {
          position: "top-right",
          timeout: 5000,
          actions: [{ text: "Info", "x-small": true, color: "secondary" }],
        }
      );

      if (notifyResult == "Info") {
        this.$dialog.error({
          text: JSON.stringify(response?.errors || response),
          title: operationDescription,
          icon: "mdi-error",
        });
      }
    },
    secondsToHms(d) {
      d = Number(d);
      var h = Math.floor(d / 3600);
      var m = Math.floor((d % 3600) / 60);
      var s = Math.floor((d % 3600) % 60);

      var hDisplay = h > 0 ? h + (h == 1 ? " hour " : " hours ") : "";
      var mDisplay = m > 0 ? m + (m == 1 ? " minute " : " minutes ") : "";
      var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";
      return hDisplay + mDisplay + sDisplay;
    },
    parseDurationString(durationString) {
      try {
        var stringPattern = /^PT(?:(\d+)D)?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d{1,3})?)S)?$/;
        var stringParts = stringPattern.exec(durationString);
        return (
          (((stringParts[1] === undefined ? 0 : stringParts[1] * 1) /* Days */ *
            24 +
            (stringParts[2] === undefined
              ? 0
              : stringParts[2] * 1)) /* Hours */ *
            60 +
            (stringParts[3] === undefined
              ? 0
              : stringParts[3] * 1)) /* Minutes */ *
            60 +
          (stringParts[4] === undefined ? 0 : stringParts[4] * 1) /* Seconds */
        );
      } catch (err) {
        console.log("parseDurationString ex", durationString, err);
        return 0;
      }
    },
    viewDay({ date }) {
      this.focus = date;
      this.type = "day";
    },
    getEventColor(event) {
      return event.color;
    },
    setToday() {
      this.focus = "";
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        setTimeout(() => (this.selectedOpen = true), 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    updateRange({ start, end }) {
      /*
      const events = [];

      const min = new Date(`${start.date}T00:00:00`);
      const max = new Date(`${end.date}T23:59:59`);
      const days = (max.getTime() - min.getTime()) / 86400000;
      const eventCount = this.rnd(days, days + 20);

      for (let i = 0; i < eventCount; i++) {
        const allDay = this.rnd(0, 3) === 0;
        const firstTimestamp = this.rnd(min.getTime(), max.getTime());
        const first = new Date(firstTimestamp - (firstTimestamp % 900000));
        const secondTimestamp = this.rnd(2, allDay ? 288 : 8) * 900000;
        const second = new Date(first.getTime() + secondTimestamp);

        events.push({
          name: this.names[this.rnd(0, this.names.length - 1)],
          start: first,
          end: second,
          color: this.colors[this.rnd(0, this.colors.length - 1)],
          timed: !allDay
        });
      }

      this.events = events;*/
    },
    rnd(a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a;
    },
  },
};
</script>